import { ResponseStatus } from '@inflearn/datasource-rest';
import { useShowNotification } from '@inflearn/ds-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { ApiError } from '../../resources/ApiError';
import { logger } from '../../resources/Logger';
import { MY_URL } from '../constants/urls';
import { useUserDevicesIdRemoveCreateMutation } from '../mutations/useUserDevicesIdRemoveCreateMutation';
import { getUserDevicesQueryKey } from '../queries/useUserDevicesQuery';
import { getDeviceVerifyCookie, removeDeviceVerifyCookie } from '../utils/deviceVerifyCookie';
import { useDeviceDeleteGuardModal } from './useDeviceDeleteGuardModal';

interface UseDeviceDeleteProps {
  deviceId: number;
  shouldRemoveQueryParam?: boolean;
}

export function useDeviceDelete({
  deviceId,
  shouldRemoveQueryParam = false,
}: UseDeviceDeleteProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showNotification } = useShowNotification();
  const router = useRouter();
  const verifiedCookie = getDeviceVerifyCookie() || '';

  const userDeviceDeleteMutation = useUserDevicesIdRemoveCreateMutation({
    id: deviceId,
  });

  const { openDeleteGuardModal } = useDeviceDeleteGuardModal();

  const deleteDevice = async () => {
    try {
      await userDeviceDeleteMutation.mutateAsync({
        token: verifiedCookie,
      });

      // 성공 시 처리
      removeDeviceVerifyCookie();
      showNotification({ title: t('마이페이지_기기관리_알림_삭제완료'), type: 'success' });
      queryClient.invalidateQueries({
        queryKey: getUserDevicesQueryKey(),
      });

      // 쿼리 파라미터 제거가 필요한 경우 (DeviceManagementPage에서 사용시)
      if (shouldRemoveQueryParam) {
        const { deviceId: deviceIdParam, ...restQuery } = router.query;
        router.replace(
          {
            pathname: router.pathname,
            query: restQuery,
          },
          undefined,
          { shallow: true },
        );
      }
    } catch (error) {
      if (ApiError.isApiError(error)) {
        if (error.errorCode === 'MAX_REMOVED_DEVICES_CURRENT_MONTH') {
          openDeleteGuardModal();
          return;
        }

        if (error.statusCode === ResponseStatus.BAD_REQUEST) {
          removeDeviceVerifyCookie();
        }

        if (!verifiedCookie) {
          router.push({
            pathname: MY_URL.DEVICE_VERIFY_REQUEST(),
            query: {
              from: router.query.from ?? 'my',
              deviceId: deviceId,
            },
          });
          return;
        }

        logger.error(error);

        showNotification({
          title: t('오류가 발생했습니다. 잠시 후 다시 시도해 주세요.'),
          type: 'error',
        });
      }
    }
  };

  return {
    deleteDevice,
    isDeleting: userDeviceDeleteMutation.isLoading,
    verifiedCookie,
  };
}
